"use client";

import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, ImagePlus, Search, Sparkles, Trophy } from "lucide-react";
import { useTranslations } from "next-intl";
import { useEffect, useMemo, useRef, useState } from "react";

import { DiscoverySkeletonGrid } from "@/components/discovery-skeleton";
import { Link } from "@/i18n/navigation";
import { getBestApps, getNewApps } from "@/lib/api";
import { ProjectSummary } from "@/types/project";
import { ProjectCard } from "@/components/project-card";

const TABS = ["new", "best"] as const;
type Tab = (typeof TABS)[number];

export function DiscoveryDashboard() {
  const t = useTranslations("Discovery");
  const [tab, setTab] = useState<Tab>("new");
  const [queryInput, setQueryInput] = useState("");
  const [query, setQuery] = useState("");
  const [newApps, setNewApps] = useState<ProjectSummary[]>([]);
  const [bestApps, setBestApps] = useState<ProjectSummary[]>([]);
  const [availableTags, setAvailableTags] = useState<string[]>([]);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestCounter = useRef(0);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setQuery(queryInput.trim());
    }, 220);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [queryInput]);

  useEffect(() => {
    async function loadTags() {
      try {
        const [allNewApps, allBestApps] = await Promise.all([getNewApps(), getBestApps()]);
        const tags = new Set<string>();

        for (const project of [...allNewApps, ...allBestApps]) {
          for (const tag of project.action_tags) {
            tags.add(tag);
          }
        }

        setAvailableTags(Array.from(tags).sort((a, b) => a.localeCompare(b)));
      } catch {
        setAvailableTags([]);
      }
    }

    void loadTags();
  }, []);

  useEffect(() => {
    async function load() {
      const requestId = ++requestCounter.current;

      try {
        setIsRefreshing(true);
        setError(null);
        const [newResponse, bestResponse] = await Promise.all([getNewApps(query), getBestApps(query)]);

        if (requestId !== requestCounter.current) {
          return;
        }

        setNewApps(newResponse);
        setBestApps(bestResponse);
      } catch {
        if (requestId !== requestCounter.current) {
          return;
        }
        setError(t("error"));
      } finally {
        if (requestId === requestCounter.current) {
          setIsRefreshing(false);
          setIsInitialLoading(false);
        }
      }
    }

    void load();
  }, [query, t]);

  const items = useMemo(() => (tab === "new" ? newApps : bestApps), [tab, newApps, bestApps]);
  const filteredItems = useMemo(() => {
    if (selectedTags.length === 0) {
      return items;
    }

    return items.filter((project) => selectedTags.some((tag) => project.action_tags.includes(tag)));
  }, [items, selectedTags]);

  function toggleTag(tag: string) {
    setSelectedTags((previous) => {
      if (previous.includes(tag)) {
        return previous.filter((value) => value !== tag);
      }

      return [...previous, tag];
    });
  }

  return (
    <main className="mx-auto w-full max-w-6xl px-3 py-5 sm:px-5">
      <header className="py-1">
        <div className="flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-50">{t("title")}</h1>
            {/* <p className="mt-3 max-w-2xl text-zinc-400">{t("subtitle")}</p> */}
          </div>
          <Link
            href="/submit"
            className="inline-flex items-center gap-2 rounded-xl bg-emerald-500 px-4 py-3 text-sm font-semibold text-zinc-950 transition hover:bg-emerald-300"
          >
            <ImagePlus className="h-4 w-4" />
            {t("submitApp")}
          </Link>
        </div>

        <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="inline-flex rounded-xl border border-zinc-300 bg-zinc-100 p-1 dark:border-zinc-700 dark:bg-zinc-900">
            {TABS.map((current) => (
              <button
                key={current}
                type="button"
                onClick={() => setTab(current)}
                className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm capitalize transition ${
                  tab === current
                    ? "bg-zinc-200 text-zinc-900 dark:bg-zinc-700 dark:text-zinc-100"
                    : "text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-100"
                }`}
              >
                {current === "new" ? <Sparkles className="h-3.5 w-3.5" /> : <Trophy className="h-3.5 w-3.5" />}
                {current === "new" ? t("newApps") : t("bestApps")}
              </button>
            ))}
          </div>

          <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center sm:justify-end">
            <div className="relative w-full sm:max-w-md">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500 dark:text-zinc-500" />
              <input
                value={queryInput}
                onChange={(event) => setQueryInput(event.target.value)}
                placeholder={t("searchPlaceholder")}
                className="w-full rounded-xl border border-zinc-300 bg-white py-2 pl-9 pr-4 text-sm text-zinc-900 outline-none ring-zinc-500/25 placeholder:text-zinc-500 focus:ring dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100 dark:ring-zinc-400/35 dark:placeholder:text-zinc-500"
              />
            </div>

            <details className="group relative w-full sm:w-72">
              <summary className="flex cursor-pointer list-none items-center justify-between gap-2 rounded-xl border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100 [&::-webkit-details-marker]:hidden">
                <div className="flex min-w-0 items-center gap-2">
                  <span className="truncate">{t("badgeFilterLabel")}</span>
                  <span className="truncate text-zinc-500 dark:text-zinc-400">
                    {selectedTags.length > 0
                      ? t("badgeFilterSelected", { count: selectedTags.length })
                      : t("badgeFilterAll")}
                  </span>
                </div>
                <ChevronDown className="h-4 w-4 shrink-0 text-zinc-500 transition group-open:rotate-180 dark:text-zinc-400" />
              </summary>

              <div className="absolute right-0 z-20 mt-2 w-full rounded-xl border border-zinc-300 bg-white p-2 shadow-xl dark:border-zinc-700 dark:bg-zinc-950">
                <div className="max-h-56 overflow-auto px-1 py-1">
                  {availableTags.length === 0 ? (
                    <p className="px-2 py-1.5 text-sm text-zinc-500">{t("badgeFilterEmpty")}</p>
                  ) : (
                    <div className="flex flex-wrap gap-2 leading-relaxed">
                      {availableTags.map((tag) => {
                        const isSelected = selectedTags.includes(tag);

                        return (
                          <button
                            key={tag}
                            type="button"
                            onClick={() => toggleTag(tag)}
                            className={`rounded-full border px-3 py-1 text-sm transition ${
                              isSelected
                                ? "border-emerald-400 bg-emerald-100 text-emerald-900 dark:border-emerald-500 dark:bg-emerald-500/20 dark:text-emerald-200"
                                : "border-zinc-300 bg-zinc-100 text-zinc-700 hover:border-zinc-400 hover:bg-zinc-200 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300 dark:hover:border-zinc-500"
                            }`}
                          >
                            {tag}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>

                {selectedTags.length > 0 ? (
                  <button
                    type="button"
                    onClick={() => setSelectedTags([])}
                    className="mt-2 w-full rounded-lg border border-zinc-300 px-2 py-1.5 text-sm text-zinc-700 transition hover:border-zinc-400 hover:text-zinc-950 dark:border-zinc-700 dark:text-zinc-300 dark:hover:border-zinc-500 dark:hover:text-zinc-100"
                  >
                    {t("badgeFilterClear")}
                  </button>
                ) : null}
              </div>
            </details>
          </div>
        </div>
      </header>

      <section className="relative mt-5">
        <AnimatePresence>
          {isRefreshing && !isInitialLoading ? (
            <motion.div
              className="pointer-events-none absolute -top-1 left-0 right-0 h-1 overflow-hidden rounded-full bg-zinc-200 dark:bg-zinc-900"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <motion.div
                className="h-full w-1/3 rounded-full bg-zinc-500 dark:bg-zinc-400"
                animate={{ x: ["-30%", "280%"] }}
                transition={{ duration: 1.1, ease: "easeInOut", repeat: Infinity }}
              />
            </motion.div>
          ) : null}
        </AnimatePresence>

        {error ? <p className="text-sm text-rose-700 dark:text-rose-300">{error}</p> : null}

        {isInitialLoading ? <DiscoverySkeletonGrid /> : null}

        {!isInitialLoading && !error && filteredItems.length === 0 ? (
          <div className="rounded-2xl border border-zinc-300 bg-white/80 p-8 text-zinc-600 dark:border-zinc-800 dark:bg-zinc-950/70 dark:text-zinc-400">{t("noApps")}</div>
        ) : null}

        {!isInitialLoading ? (
          <motion.div layout className="grid grid-cols-1 gap-3.5 md:grid-cols-2 2xl:grid-cols-3">
            <AnimatePresence mode="popLayout">
              {filteredItems.map((project) => (
                <motion.div
                  key={project.id}
                  layout
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.2, ease: "easeOut" }}
                >
                  <ProjectCard project={project} />
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>
        ) : null}
      </section>
    </main>
  );
}
